import PropTypes from 'prop-types';
import React, { forwardRef, useState, useEffect, useMemo, useRef } from 'react';
// @mui
import { Box, Dialog, Card, List, ListItem, ListItemText, Typography, Slide, AppBar, Button } from '@mui/material';
// components
import { setCurrentTreatmentDataGlobal } from 'src/redux/slices/multiTreatment';
import { Iconify } from '..';
import { NormalLoading } from '../loading';
import { CallScriptV2 } from '../callScriptV2';
import { transformScriptData } from '../callScriptV2/utils';
import { clearLead, clearTaskLead, followUpAppointmentHandler, updateBookingSteps, tableLead, followUpSlice, updateSteps, contactLeadSlice, updateLeadDetail, updateCompleteStatus } from '../../redux/slices/lead';
import { dispatch, useSelector } from '../../redux/store';
import { getInboundScripts, setCallScriptSection } from '../../redux/slices/callScript';
import { callApi } from '../../utils/apiCall';
import { useToggle } from '../../hooks';
import { localStorageSetItem, localStorageRemoveItem } from '../../utils/storage-available';
import OpenAppointmentNotBookedPopup from './OpenAppointmentNotBookedPopup';
import OpenContactLeadPopup from './OpenContactLeadPopup';
import { TreatmentAndAppointment, PersonalDetails, PaymentOption, MessageAndReminder } from '../../sections/@dashboard/general/app';
import {
    commonDialogHeaderStyle,
    reminderTitleStyle,
    cancelBoxStyle,
} from '../../styles/ScheduleReminder';

const Transition = forwardRef((props, ref) => <Slide direction='up' ref={ref} {...props} />);

/**
 * OpenCallScriptPopup - Inbound Call Script Popup
 *
 * This component displays inbound call scripts using the new CallScriptV2 UI.
 * Used when a new inbound call comes in and the user needs to fill out a script.
 */
const OpenCallScriptPopup = ({ open, onClose }) => {
    const { followLead } = useSelector((state) => state.lead);
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    // Use prefetched inbound scripts from Redux (fetched on dashboard load)
    const { inboundScriptsData } = useSelector((state) => state.callScript);

    const [selectedScript, setSelectedScript] = useState(null);
    const [leadId, setLeadId] = useState('');
    const [callScriptReloadPayLoad, setCallScriptReloadPayLoad] = useState({});
    const [validStep, setValidStep] = useState(false);
    const [bookingAttendance, setBookingAttendance] = useState(null);
    const [bookingSteps, setBookingSteps] = useState([]);
    const [scrollableView, setScrollableView] = useState(false);

    // Fetch call script config from Practice Choices
    useEffect(() => {
        if (!practiceId || !open) return;
        callApi(`/api/v1/practices/${practiceId}/choices/?ordering=-created_at`)
            .then((res) => {
                if (res.ok) {
                    const choices = res.data?.results || res.data || [];
                    const callScriptChoice = choices.find((c) => {
                        const m = typeof c?.metadata === 'string'
                            ? (() => { try { return JSON.parse(c.metadata || '{}'); } catch { return {}; } })()
                            : (c?.metadata || {});
                        return m.type === 'call_script_config';
                    });
                    if (callScriptChoice) {
                        const meta = typeof callScriptChoice.metadata === 'string'
                            ? JSON.parse(callScriptChoice.metadata)
                            : callScriptChoice.metadata;
                        setScrollableView(meta?.default_view === 'scrollable');
                    }
                }
            })
            .catch((err) => { console.error('OpenCallScriptPopup: choices fetch failed', err); });
    }, [practiceId, open]);

    // Get Redux state for booking flow step management
    // eslint-disable-next-line no-unused-vars
    const { isAppointmentBooked: reduxAppointmentBooked, attendance, markAsComplete, followUpLead: followUpLeadStatus } = useSelector((state) => state.lead);

    // Ref to prevent auto-select when closing
    const isClosingRef = useRef(false);
    // Ref to track when we're transitioning to a booking popup (to prevent closing everything)
    const isBookingTransitionRef = useRef(false);
    // Ref for form submission in booking flow
    const inputRef = useRef(null);
    // Ref to prevent duplicate updateLeadDetail dispatch (Priority 12 fix)
    const hasDispatchedLeadDetailRef = useRef(false);

    // Appointment popup toggles
    const {
        toggle: isAppointmentNotBooked,
        onOpen: onOpenAppointmentNotBooked,
        onClose: onCloseAppointmentNotBooked
    } = useToggle();

    const {
        toggle: isBookingPopupOpen,
        onOpen: onOpenBookingPopup,
        onClose: onCloseBookingPopup
    } = useToggle();

    // If scripts not prefetched yet, fetch them when popup opens
    useEffect(() => {
        if (open && practiceId && !inboundScriptsData) {
            dispatch(getInboundScripts(practiceId));
        }
        if (open) {
            isClosingRef.current = false;
        }
    }, [open, practiceId, inboundScriptsData]);

    // Clear any stale lead data when popup opens (prevents lead details screen data from leaking)
    useEffect(() => {
        if (open) {
            dispatch(clearLead());
            dispatch(clearTaskLead());
            dispatch(setCurrentTreatmentDataGlobal(null));
            dispatch(followUpAppointmentHandler(null));
            dispatch(updateBookingSteps(null));
            dispatch(updateSteps(null));
            dispatch(tableLead(null));
            dispatch(contactLeadSlice(true));
            // Defensive: outbound's CallScriptContainer can leave callScriptData populated
            // if its internal clearing effect early-returns (no treatment selected yet),
            // which then breaks every subsequent inbound open until a hard refresh.
            dispatch(setCallScriptSection(null));
        }
    }, [open]);

    // For inbound: get non-default scripts that have at least one enabled field somewhere,
    // sorted by treatments count (most treatments first).
    //
    // Empty scripts (sections with no fields, or all fields disabled) are filtered out:
    // they would render as the closure section with a "No lead selected" warning, which
    // looks like a frontend bug but is actually a script-authoring/data issue. Filtering
    // here prevents both auto-selection and manual selection of structurally empty scripts.
    const scriptList = useMemo(() => {
        const usable = inboundScriptsData?.results?.filter((item) => {
            if (item.is_default) return false;
            const hasContent = (item.sections || []).some(
                (sec) => (sec.fields || []).some((f) => f?.is_enabled)
            );
            return hasContent;
        });
        // Sort by treatments count descending (most treatments first)
        return usable?.sort((a, b) => {
            const treatmentsA = a.treatments?.length || 0;
            const treatmentsB = b.treatments?.length || 0;
            return treatmentsB - treatmentsA;
        });
    }, [inboundScriptsData?.results]);

    // Auto-select the script with most treatments (first in sorted list)
    useEffect(() => {
        if (open && scriptList?.length > 0 && !selectedScript && !isClosingRef.current) {
            setSelectedScript(scriptList[0]);
        }
    }, [open, scriptList, selectedScript]);

    // Reset selected script when dialog closes
    useEffect(() => {
        if (!open) {
            isClosingRef.current = true;
            setSelectedScript(null);
        }
    }, [open]);

    // Handle appointment creation when markAsComplete becomes true (same as outbound flow)
    // This triggers the actual API call to create the appointment after the booking flow is completed
    useEffect(() => {
        if (markAsComplete && isBookingPopupOpen && !hasDispatchedLeadDetailRef.current) {
            // Set guard immediately to prevent double-dispatch from re-renders
            hasDispatchedLeadDetailRef.current = true;
            // Reset markAsComplete first to prevent duplicate calls
            dispatch(updateCompleteStatus(false));
            // Call updateLeadDetail to create the appointment (same as outbound 'contactLead' flow)
            dispatch(updateLeadDetail(reduxAppointmentBooked, appointmentBookedClose));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [markAsComplete, isBookingPopupOpen, reduxAppointmentBooked]);

    // Helper to clear all local and Redux state
    const clearAllState = () => {
        // Clear local state
        setSelectedScript(null);
        setLeadId('');
        setCallScriptReloadPayLoad({});
        setValidStep(false);
        setBookingAttendance(null);
        setBookingSteps([]);
        // Reset booking transition ref so Cancel button works on reopen (Priority 7 fix)
        isBookingTransitionRef.current = false;
        // Reset dispatch guard for appointment creation (Priority 12 fix)
        hasDispatchedLeadDetailRef.current = false;

        // Clear localStorage treatment data (used by TreatmentAndAppointment)
        localStorageRemoveItem('localTreatmentData');

        // Clear Redux state
        dispatch(clearLead());
        dispatch(clearTaskLead());
        dispatch(setCurrentTreatmentDataGlobal(null));
        dispatch(followUpAppointmentHandler(null));
        dispatch(updateBookingSteps(null));
        dispatch(updateSteps(null));
        dispatch(tableLead(null));
        dispatch(contactLeadSlice(true));
        // Symmetrical with CallScriptContainer.handleClose — never leave the script
        // payload in Redux for the next session to inherit.
        dispatch(setCallScriptSection(null));
    };

    const handleClose = (reason, reloadPayload) => {
        // Don't close if we're transitioning to a booking popup
        if (isBookingTransitionRef.current) {
            // Don't reset the ref here - keep it true until booking flow completes
            return;
        }

        // Set closing flag first to prevent race conditions
        isClosingRef.current = true;

        // Always clear all state when closing (both finish and cancel)
        clearAllState();

        onClose(reason, reloadPayload);
    };

    const handleScriptClick = (script) => {
        setSelectedScript(script);
    };

    // Transform script data for CallScriptV2
    const v2ScriptData = useMemo(() => {
        if (!selectedScript) return null;
        return transformScriptData(selectedScript, 'inbound');
    }, [selectedScript]);

    // Lead data for context panel
    const v2LeadData = useMemo(() => ({
        name: followLead?.first_name
            ? `${followLead.first_name} ${followLead.last_name || ''}`.trim()
            : 'Unknown Caller',
        initials: followLead?.first_name
            ? `${followLead.first_name[0]}${followLead.last_name?.[0] || ''}`.toUpperCase()
            : '?',
        tags: followLead?.treatment
            ? [{ label: followLead.treatment, variant: 'treatment' }]
            : [],
    }), [followLead]);

    // Handle script completion
    const handleComplete = (data) => {
        handleClose('finish', data);
    };

    // Handle appointment booked event from call script
    const handleAppointmentBooked = (data) => {
        // Set flag to prevent closing the entire popup when onClose is called
        isBookingTransitionRef.current = true;

        // Extract data from call script form - prioritize form data over followLead
        const formData = data?.formData || {};
        const currentLeadId = formData.lead_id || followLead?.id;
        const currentTreatmentId = formData.treatment_id || followLead?.lead_treatments?.[0]?.id;

        // Get treatment info from call script form (user may have selected/changed treatment)
        const formTreatmentName = formData.treatment || formData.treatmentInterest || '';
        const formTreatmentPrice = formData.treatmentValue || formData.treatment_price || formData.treatmentInterestValue || 0;
        const formTeethAmount = formData.teethAmount || formData.treatmentInterestTeeth || null;

        if (currentLeadId) setLeadId(currentLeadId);
        setCallScriptReloadPayLoad(data);

        // Set up Redux state for the full booking flow (same as outbound)
        dispatch(followUpAppointmentHandler('Booking Details'));
        dispatch(updateBookingSteps('Booking Details'));

        // Set up tableRowLead for the booking flow - use call script form data for treatment info
        const leadTreatment = followLead?.lead_treatments?.[0] || {};
        dispatch(tableLead({
            lead_id: currentLeadId || '',
            first_name: formData.first_name || followLead?.first_name || '',
            last_name: formData.last_name || followLead?.last_name || '',
            treatment_id: currentTreatmentId || '',
            treatment_name: formTreatmentName || leadTreatment.treatment || '',
            price: parseFloat(formTreatmentPrice) || (leadTreatment.custom_price ?? leadTreatment.price ?? 0),
            custom_price: parseFloat(formTreatmentPrice) || (leadTreatment.custom_price ?? 0),
            leadTreatmentSpecification: formTeethAmount ? { teeth_count: formTeethAmount } : (leadTreatment.leadtreatment_specification || null),
            treatmentData: followLead?.lead_treatments || [],
            status: leadTreatment.status || '',
        }));

        // Fetch full follow up data for the booking flow
        // If this fails, the booking popup still opens but may have partial data
        if (practiceId && currentLeadId) {
            try {
                dispatch(followUpSlice(currentLeadId, practiceId));
            } catch (err) {
                console.error('Failed to fetch follow-up data for booking:', err);
            }
        }

        // Store treatment data in localStorage for TreatmentAndAppointment to read
        // The component reads from localStorage in a useEffect to populate form fields
        const treatmentNameForStorage = formTreatmentName || leadTreatment.treatment || '';
        const treatmentPriceForStorage = parseFloat(formTreatmentPrice) || (leadTreatment.custom_price ?? leadTreatment.price ?? 0);
        localStorageSetItem('localTreatmentData', JSON.stringify({
            treatment: treatmentNameForStorage,
            teethAmount: formTeethAmount,
            price: treatmentPriceForStorage
        }));

        // NOTE: Don't set bookingAttendance here - it would cause OpenContactLeadPopup
        // to increment activeStep by 1, but we only have the booking step at index 0.
        // The booking step will show correctly at index 0 without the increment.

        // Build ALL booking flow steps upfront to avoid timing issues with step progression.
        // If we only build the first step, the validStep useEffect in OpenContactLeadPopup
        // might see only 1 step and close the popup prematurely.
        setBookingSteps([
            { label: 'Booking Details', component: <TreatmentAndAppointment ref={inputRef} type="followUp" handleNext={(ok) => updateStep(!!ok)} isOverview /> },
            { label: 'Personal Details', component: <PersonalDetails ref={inputRef} handleNext={(ok) => updateStep(!!ok)} /> },
            { label: 'Payment options', component: <PaymentOption ref={inputRef} handleNext={(ok) => updateStep(!!ok)} /> },
            { label: 'Message review & reminders', component: <MessageAndReminder ref={inputRef} handleNext={(ok) => updateStep(!!ok)} type="FollowUp" /> },
        ]);

        // Close the call script and open booking popup
        setSelectedScript(null);
        onOpenBookingPopup();
    };

    // Handle appointment not booked event from call script
    const handleAppointmentNotBooked = (data) => {
        // Set flag to prevent closing the entire popup when onClose is called
        isBookingTransitionRef.current = true;
        // Set lead and treatment IDs from the data or followLead
        const currentLeadId = data?.formData?.lead_id || followLead?.id;
        if (currentLeadId) setLeadId(currentLeadId);
        setCallScriptReloadPayLoad(data);
        // Close the call script and open not-booked popup
        setSelectedScript(null);
        onOpenAppointmentNotBooked();
    };

    // Close handlers for appointment popups
    const appointmentNotBookedClose = () => {
        const reloadData = callScriptReloadPayLoad;
        // Reset transition ref now that flow is complete
        isBookingTransitionRef.current = false;
        onCloseAppointmentNotBooked();
        clearAllState();
        onClose('finish', reloadData);
    };

    const onAppointmentNotBookClose = () => {
        // Reset transition ref
        isBookingTransitionRef.current = false;
        onCloseAppointmentNotBooked();
        clearAllState();
    };

    const appointmentBookedClose = () => {
        const reloadData = callScriptReloadPayLoad;
        // Reset transition ref now that booking flow is complete
        isBookingTransitionRef.current = false;
        onCloseBookingPopup();
        clearAllState();
        onClose('finish', reloadData);
    };

    // Handler to update step validation (used by booking flow)
    const updateStep = (valid) => {
        setValidStep(valid);
    };

    // Build user object for OpenContactLeadPopup from followLead
    const bookingUser = useMemo(() => {
        if (!followLead) return null;
        return {
            ...followLead,
            lead_id: followLead.id,
        };
    }, [followLead]);

    // REMOVED: Redundant useEffect that watched reduxAppointmentBooked and duplicated
    // handleAppointmentBooked's step building. If reduxAppointmentBooked was stale from
    // a prior session, it caused the booking popup to auto-open on mount. (Priority 11 fix)

    // Render script selection list
    const renderScriptList = () => (
        <>
            <AppBar sx={commonDialogHeaderStyle}>
                <Typography
                    sx={reminderTitleStyle}
                    variant='h6'
                    component='div'
                >
                    Call Script
                </Typography>
                <Box sx={cancelBoxStyle}>
                    <Button
                        color='inherit'
                        variant='outlined'
                        sx={{ borderColor: 'common.white', color: 'common.white' }}
                        onClick={() => handleClose('cancel')}
                    >
                        Cancel
                    </Button>
                </Box>
            </AppBar>
            <Box sx={{
                height: 'calc(100% - 64px)',
                bgcolor: '#F4F6F8',
                mt: '64px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 3,
            }}>
                <NormalLoading
                    tableData={!inboundScriptsData ? 'loading' : (scriptList?.length === 0 && 'No Found')}
                    pageCount={!inboundScriptsData ? 'loading' : (scriptList?.length === 0 && 'No Found')}
                    type={'treatment'}
                />
                <Card sx={{
                    width: '100%',
                    maxWidth: '550px',
                    borderRadius: '16px',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                    overflow: 'hidden',
                }}>
                    <Box sx={{
                        p: 3,
                        pb: 2,
                        textAlign: 'center',
                        borderBottom: '1px solid rgba(145, 158, 171, 0.16)',
                    }}>
                        <Box sx={{
                            width: 56,
                            height: 56,
                            borderRadius: '50%',
                            bgcolor: 'rgba(84, 214, 44, 0.16)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                            mb: 2,
                        }}>
                            <Iconify icon="mdi:phone-incoming" width={28} height={28} sx={{ color: '#54D62C' }} />
                        </Box>
                        <Typography sx={{ fontSize: '20px', fontWeight: '700', color: '#212B36' }}>
                            Select a Script
                        </Typography>
                        <Typography sx={{ fontSize: '14px', color: '#637381', mt: 0.5 }}>
                            Choose an inbound script to continue
                        </Typography>
                    </Box>
                    <Box sx={{ p: 2 }}>
                        <List sx={{ m: 0, p: 0 }}>
                            {scriptList?.map((script, index) => (
                                <ListItem
                                    key={script.id || index}
                                    onClick={() => handleScriptClick(script)}
                                    sx={{
                                        p: 2,
                                        mb: 1,
                                        borderRadius: '12px',
                                        bgcolor: '#F4F6F8',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            bgcolor: '#E8F5E9',
                                            transform: 'translateX(4px)',
                                        },
                                        '&:last-child': { mb: 0 },
                                    }}
                                >
                                    <Box sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: '10px',
                                        bgcolor: 'white',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mr: 2,
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                                    }}>
                                        <Iconify icon="mdi:script-text-outline" width={22} height={22} sx={{ color: '#54D62C' }} />
                                    </Box>
                                    <ListItemText
                                        primary={script.name}
                                        sx={{
                                            '.MuiTypography-root': {
                                                fontWeight: '600',
                                                fontSize: '15px',
                                                color: '#212B36',
                                            }
                                        }}
                                    />
                                    <Iconify
                                        icon="eva:arrow-ios-forward-fill"
                                        width={20}
                                        height={20}
                                        sx={{ color: '#919EAB' }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                </Card>
            </Box>
        </>
    );

    // Don't show main dialog content when booking popup is open
    const showMainContent = !isBookingPopupOpen && !isAppointmentNotBooked;

    return (
        <>
            <Dialog
                fullScreen
                TransitionComponent={Transition}
                open={open && showMainContent}
                onClose={(e, reason) => {
                    // Prevent closing on backdrop click or escape key
                    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                        return;
                    }
                    handleClose('cancel');
                }}
                sx={{ '& .MuiDialog-paper': { bgcolor: '#F4F6F8' } }}
            >
                {selectedScript && v2ScriptData ? (
                    <CallScriptV2
                        // Force a fresh CallScriptV2 instance per inbound script. Belt-and-braces
                        // against any future local-state leakage between sessions.
                        key={`inbound-${v2ScriptData?.id ?? selectedScript?.id ?? 'none'}`}
                        scriptData={v2ScriptData}
                        leadData={v2LeadData}
                        followLead={followLead}
                        type="inbound"
                        scriptCategory="INBOUND"
                        onClose={(e, reason) => {
                            if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                                return;
                            }
                            // If we're transitioning to a booking popup, don't close everything
                            if (isBookingTransitionRef.current) {
                                // Don't reset the ref here - keep it true until booking flow completes
                                return;
                            }
                            // Otherwise close the entire popup when X is clicked
                            handleClose('cancel');
                        }}
                        onSave={() => {}}
                        onComplete={handleComplete}
                        onAppointmentBooked={handleAppointmentBooked}
                        onAppointmentNotBooked={handleAppointmentNotBooked}
                        scrollableView={scrollableView}
                    />
                ) : (
                    renderScriptList()
                )}
            </Dialog>

            {/* Appointment Not Booked Popup - Rendered OUTSIDE main dialog */}
            {isAppointmentNotBooked && (
                <OpenAppointmentNotBookedPopup
                    open={isAppointmentNotBooked}
                    headerButtons
                    onClose={appointmentNotBookedClose}
                    leadId={leadId}
                    onAppointmentNotBookClose={onAppointmentNotBookClose}
                />
            )}

            {/* Appointment Booked Popup - Full multi-step flow like outbound */}
            {isBookingPopupOpen && (
                <OpenContactLeadPopup
                    open
                    headerButtons
                    onClose={appointmentBookedClose}
                    user={bookingUser || followLead}
                    type="followup"
                    inputRef={inputRef}
                    validStep={validStep}
                    updateStep={updateStep}
                    BookingAttendance={bookingAttendance}
                    steps={bookingSteps}
                    bookingOnly
                />
            )}
        </>
    );
};

OpenCallScriptPopup.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
};

export default OpenCallScriptPopup;
